import React, { useEffect, useRef, useState } from "react";
import { ChevronDownRegular, CheckmarkRegular } from "@fluentui/react-icons";
import "../styles/welcome.css";
import ToolbarTooltipButton from "./ToolbarTooltipButton";
import type {
  AttachedFile,
  ChatMessage,
} from "../../type";
import {
  QUICK_PROMPTS_USERS,
  QUICK_PROMPTS_ANSWERS,
  MODEL_OPTIONS,
  AGENT_OPTIONS,
  AgentOption,
  ModelOption,
  Data_history,
} from "../../staticdata";

const MESSAGE_STORAGE_KEY = "fp.ai.welcome.messages";

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const Welcome: React.FC = () => {
  const [draftPrompt, setDraftPrompt] = useState("");
  const [isAgentMenuOpen, setIsAgentMenuOpen] = useState(false);
  const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
  const [selectedAgentOption, setSelectedAgentOption] = useState<AgentOption>(AGENT_OPTIONS[0]);
  const [selectedModelOption, setSelectedModelOption] = useState<ModelOption>(MODEL_OPTIONS[0]);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const agentMenuRef = useRef<HTMLDivElement | null>(null);
  const modelMenuRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      const storedMessages = window.localStorage.getItem(MESSAGE_STORAGE_KEY);

      if (storedMessages) {
        setMessages(JSON.parse(storedMessages) as ChatMessage[]);
      }
    } catch {
      window.localStorage.removeItem(MESSAGE_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(MESSAGE_STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    chatScrollRef.current?.scrollTo({
      top: chatScrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      if (!agentMenuRef.current?.contains(target)) {
        setIsAgentMenuOpen(false);
      }

      if (!modelMenuRef.current?.contains(target)) {
        setIsModelMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handlePromptClick = () => {
    setIsAgentMenuOpen((current) => !current);
    setIsModelMenuOpen(false);
  };
  const handleRefreshConversation = () => {
    setMessages([]);
    setDraftPrompt("");
    setAttachedFiles([]);
    window.localStorage.removeItem(MESSAGE_STORAGE_KEY);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleModelMenuToggle = () => {
    setIsModelMenuOpen((current) => !current);
    setIsAgentMenuOpen(false);
  };

  const handleAgentOptionSelect = (option: AgentOption) => {
    setSelectedAgentOption(option);
    setIsAgentMenuOpen(false);
  };

  const handleModelOptionSelect = (option: ModelOption) => {
    setSelectedModelOption(option);
    setIsModelMenuOpen(false);
  };

  const handleAddMedia = () => {
    fileInputRef.current?.click();
  };

  const previewFile = attachedFiles.find((file) => file.preview);

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) {
      return;
    }

    const nextFiles = await Promise.all(
      Array.from(files).map(async (file) => ({
        name: file.name,
        type: file.type,
        extension: file.name.split(".").pop()?.toUpperCase() ?? "",
        preview: file.type.startsWith("image/") ? await readFileAsDataUrl(file) : undefined,
      }))
    );

    setAttachedFiles(nextFiles);
  };

  const handleHistoryToggle = () => {
    setIsHistoryOpen((current) => !current);
  };

  const handleSendMessage = () => {
    const trimmedPrompt = draftPrompt.trim();

    if (!trimmedPrompt && attachedFiles.length === 0) {
      return;
    }

    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: `${Date.now()}`,
        text: trimmedPrompt,
        attachments: attachedFiles,
      },
    ]);

    setDraftPrompt("");
    setAttachedFiles([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }

    if (!draftPrompt.trim() && attachedFiles.length === 0) {
      return;
    }

    event.preventDefault();
    handleSendMessage();
  };

  const handlesendbtndisabled = !draftPrompt.trim() && attachedFiles.length === 0;

  return (
    <div className="welcome-container">
      <div className={`welcome-shell${isHistoryOpen ? " welcome-shell-history" : ""}`}>
        <header className="welcome-topbar">
          <img src="assets/logo-new.png" alt="Feasibility" className="welcome-logo" />
          <div className="topbar-actions">
            <button
              onClick={handleRefreshConversation}
              type="button"
              className="icon-button"
              aria-label="Add new workspace"
            >
              <img src="assets/AddIcon.svg" alt="Add" />
            </button>

            <button
              type="button"
              className="icon-button"
              aria-label="Show history"
              onClick={handleHistoryToggle}
            >
              <img src="assets/refreshIcon.svg" alt="History" />
            </button>
            <button type="button" className="user-chip" aria-label="User profile">
              <span className="user-avatar">
                <img src="assets/UserIcon.svg" alt="User" />
              </span>
              <span>User</span>
            </button>
          </div>
        </header>

        {isHistoryOpen ? (
          <div className="history-panel" aria-label="Conversation history">
            {Data_history.map((historyItem) => (
              <ToolbarTooltipButton
                key={historyItem.id}
                label={historyItem.question}
                tooltip={`Question: ${historyItem.question}\nAnswer: ${historyItem.answer}`}
              />
            ))}
          </div>
        ) : null}
        {!isHistoryOpen ? (
          <main className="welcome-hero">
            {messages.length === 0 ? (
              <>
                <div className="hero-copy">
                  <p className="welcome-title">
                    <span className="hello">Hello!</span> I am feasibility Research Assistant
                  </p>
                </div>

                <div className="hero-content">
                  <div className="quick-prompts" aria-label="Suggested prompts">
                    {QUICK_PROMPTS_USERS.map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        className={`prompt-chip${draftPrompt === prompt ? " prompt-chip-active" : ""}`}
                        onClick={() => setDraftPrompt(prompt)}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                  <div className="quick-prompts quick-prompts-answers" aria-label="Suggested answers">
                    {QUICK_PROMPTS_ANSWERS.map((answer, index) => (
                      <div key={index} className="answer-chip">
                        {answer}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="chat-thread" ref={chatScrollRef} aria-label="Conversation messages">
                {messages.map((message) => (
                  <div key={message.id} className="chat-message chat-message-user">
                    {message.attachments.length > 0 ? (
                      <div className="chat-message-media">
                        {message.attachments.map((file) => (
                          <div key={`${message.id}-${file.name}`} className="chat-media-card">
                            <div className="chat-media-thumb">
                              {file.preview ? (
                                <img src={file.preview} alt={file.name} />
                              ) : (
                                <div className="attached-media-placeholder">
                                  {file.extension || "FILE"}
                                </div>
                              )}
                            </div>
                            <div className="chat-media-label">{file.name}</div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {message.text ? <p className="chat-message-text">{message.text}</p> : null}
                  </div>
                ))}
              </div>
            )}
          </main>
        ) : null}

        <div className="welcome-composer">
          <div className="composer-body">
            {previewFile ? (
              <div className="composer-media-preview">
                <div className="attached-media-card">
                  <div className="attached-media-thumb">
                    <img src={previewFile.preview} alt={previewFile.name} />
                  </div>
                  <div className="attached-media-label">{previewFile.name}</div>
                </div>
              </div>
            ) : null}
            <textarea
              className="composer-input"
              rows={2}
              value={draftPrompt}
              onChange={(event) => setDraftPrompt(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder="Describe us what to want to know"
            />
          </div>

          <div className="composer-footer">
            <div className="composer-tools">
              <button
                onClick={handleAddMedia}
                type="button"
                className="tool-pill tool-pill-icon"
                aria-label="Add attachment"
              >
                <img src="assets/AddImageIcon.svg" alt="Add Image" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="*"
                className="hidden-file-input"
                title="Upload media files"
                aria-label="Upload media files"
                onChange={handleFileInputChange}
              />

              <div className="tool-menu" ref={agentMenuRef}>
                <button
                  onClick={handlePromptClick}
                  type="button"
                  className={`tool-pill tool-pill-agent-icon${isAgentMenuOpen ? " tool-pill-open" : ""}`}
                  aria-haspopup="menu"
                >
                  <img src={selectedAgentOption.icon} alt="" aria-hidden="true" />
                  <span>{selectedAgentOption.label}</span>
                  <ChevronDownRegular className="tool-chevron" />
                </button>

                {isAgentMenuOpen ? (
                  <div className="tool-dropdown" role="menu" aria-label="Agent options">
                    {AGENT_OPTIONS.map((option) => (
                      <button
                        key={option.label}
                        type="button"
                        role="menuitem"
                        className={`tool-dropdown-item${selectedAgentOption.label === option.label ? " tool-dropdown-item-active" : ""}`}
                        onClick={() => handleAgentOptionSelect(option)}
                      >
                        <img src={option.icon} alt="" aria-hidden="true" />
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="tool-menu" ref={modelMenuRef}>
                <button
                  onClick={handleModelMenuToggle}
                  type="button"
                  className={`tool-pill tool-pill-model${isModelMenuOpen ? " tool-pill-open" : ""}`}
                  aria-haspopup="menu"
                >
                  <img
                    src={selectedModelOption.icon}
                    alt=""
                    aria-hidden="true"
                    className="tool-auto-icon"
                  />
                  <span>{selectedModelOption.label}</span>
                  <ChevronDownRegular className="tool-chevron" />
                </button>

                {isModelMenuOpen ? (
                  <div
                    className="tool-dropdown tool-dropdown-model"
                    role="menu"
                    aria-label="Model options"
                  >
                    {MODEL_OPTIONS.map((option) => (
                      <button
                        key={option.label}
                        role="menuitem"
                        className={`tool-dropdown-item${selectedModelOption.label === option.label ? " tool-dropdown-item-active" : ""}${option.label === "Auto" ? " tool-dropdown-item-auto" : ""}`}
                        onClick={() => handleModelOptionSelect(option)}
                      >
                        <img
                          src={option.icon}
                          alt=""
                          aria-hidden="true"
                          className="tool-dropdown-icon"
                        />
                        <span>{option.label}</span>
                        {option.label === "Auto" ? (
                          <span className="tool-dropdown-toggle" aria-hidden="true" />
                        ) : selectedModelOption.label === option.label ? (
                          <CheckmarkRegular className="tool-dropdown-check" aria-hidden="true" />
                        ) : (
                          <span />
                        )}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              className="send-button"
              aria-label="Send message"
              disabled={handlesendbtndisabled}
              onClick={handleSendMessage}
            >
              {handlesendbtndisabled ? (
                <img src="assets/DisablebtnIcon.svg" alt="Send" />
              ) : (
                <img src="assets/SendFilledIcon.svg" className="send-button" alt="Send" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;



